<!--
  @component ComposeCanvas — Phase 3 topology canvas (scope-aware).
  tier:   deployments composability surface (the "see + build what's connected")

  The composable platform made visible + editable. Projects on the left,
  resources on the right (databases + shared stores + domains), links as edges.
  Click a project to select it, then click a resource to link / unlink it; click
  an edge to remove that link. Domains are 1:N (a domain serves at most one app),
  so a domain node carries at most one edge and clicking moves the route. A
  hand-rolled SVG graph (no graph lib), Slate-Ink — mirrors FleetMapBlock.

  reads: useDeploymentIO (db.* + storage.shared.* + domain.* listOwned/list/attach/detach),
         useIdentityIO (projects), useScopeIO (which projects are nodes)
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import { useScopeIO } from "./ScopeIO.svelte";
  import { scopeProjects } from "./fleet-util.js";
  import { initial, projectGradient } from "../../home/home-util.js";

  const identity = useIdentityIO();
  const node = useDeploymentIO();
  const scope = useScopeIO();

  // peek = compact embed (Overview command center): trims the page header + hint
  // and caps height; the canvas + link/unlink affordances stay fully live.
  interface Props { peek?: boolean; }
  const { peek = false }: Props = $props();

  interface OwnedDb { id: string; originProjectId: string; engine: string; region: string; status: string; }
  interface OwnedStore { id: string; name: string; status: string; }
  interface DomainRow { domain: string; vendor: string; byo: boolean; routedToProjectId: string | null; routedToProjectName: string | null; }
  interface Link { projectId: string; projectName: string; secretName: string; }
  type ResKind = "db" | "store" | "domain";
  interface Resource { kind: ResKind; id: string; label: string; sub: string; }

  const projects = $derived(scopeProjects(identity.projects, scope.projectIds));

  let dbs = $state<OwnedDb[]>([]);
  let stores = $state<OwnedStore[]>([]);
  let domains = $state<DomainRow[]>([]);
  let linkMap = $state<Record<string, Link[]>>({});
  let loaded = $state(false);
  let err = $state<string | null>(null);
  let busy = $state<string>("");
  let selProject = $state<string | null>(null);

  function nameOf(projId: string): string { return identity.projects.find((p) => p.id === projId)?.name ?? projId; }

  // db ids ("db-…"), store ids ("sstore-…") and domain strings ("acme.com") are
  // disjoint, so one merged map keyed by resource id is safe.
  const resources = $derived.by<Resource[]>(() => [
    ...dbs.map((d) => ({ kind: "db" as const, id: d.id, label: nameOf(d.originProjectId), sub: d.engine })),
    ...stores.map((s) => ({ kind: "store" as const, id: s.id, label: s.name, sub: "shared store" })),
    ...domains.map((d) => ({ kind: "domain" as const, id: d.domain, label: d.domain, sub: "domain" })),
  ]);
  function kindOf(resId: string): ResKind | null { return resources.find((r) => r.id === resId)?.kind ?? null; }

  // A domain's single route is normalised into the same Link[] shape as db/store
  // links (0 or 1 entry), so the edge renderer treats all three kinds uniformly.
  function domainLinks(d: DomainRow): Link[] {
    return d.routedToProjectId
      ? [{ projectId: d.routedToProjectId, projectName: d.routedToProjectName ?? nameOf(d.routedToProjectId), secretName: "DOMAIN" }]
      : [];
  }

  async function reloadLinks(resId: string, kind: ResKind): Promise<void> {
    try {
      if (kind === "domain") {
        // domain.list carries every route inline — refetch and rebuild all
        // domain entries (attach moves a route, so neighbours can change too).
        const dom = (await node.dispatch("domain.list")) as DomainRow[] ?? [];
        domains = dom;
        const upd = { ...linkMap };
        for (const dr of dom) upd[dr.domain] = domainLinks(dr);
        linkMap = upd;
        return;
      }
      const r = kind === "db"
        ? (await node.dispatch("db.listLinks", { databaseId: resId })) as { links: Link[] }
        : (await node.dispatch("storage.shared.listLinks", { storeId: resId })) as { links: Link[] };
      linkMap = { ...linkMap, [resId]: r.links ?? [] };
    } catch { /* keep prior */ }
  }
  async function loadAll(): Promise<void> {
    try {
      const [d, s, dom] = await Promise.all([
        node.dispatch("db.listOwned") as Promise<OwnedDb[]>,
        node.dispatch("storage.shared.listOwned") as Promise<OwnedStore[]>,
        node.dispatch("domain.list") as Promise<DomainRow[]>,
      ]);
      dbs = d ?? []; stores = s ?? []; domains = dom ?? [];
      const m: Record<string, Link[]> = {};
      await Promise.all([
        ...(d ?? []).map(async (x) => { try { const r = (await node.dispatch("db.listLinks", { databaseId: x.id })) as { links: Link[] }; m[x.id] = r.links ?? []; } catch { m[x.id] = []; } }),
        ...(s ?? []).map(async (x) => { try { const r = (await node.dispatch("storage.shared.listLinks", { storeId: x.id })) as { links: Link[] }; m[x.id] = r.links ?? []; } catch { m[x.id] = []; } }),
      ]);
      for (const dr of dom ?? []) m[dr.domain] = domainLinks(dr);
      linkMap = m; err = null;
    } catch (e) { dbs = []; stores = []; domains = []; err = e instanceof Error ? e.message : String(e); }
    finally { loaded = true; }
  }
  $effect(() => { void loadAll(); });

  function linksOf(resId: string): Link[] { return linkMap[resId] ?? []; }
  function isLinked(projId: string, resId: string): boolean { return linksOf(resId).some((l) => l.projectId === projId); }
  function secretFor(projId: string, resId: string): string { return linksOf(resId).find((l) => l.projectId === projId)?.secretName ?? "DATABASE_URL"; }

  async function toggle(projId: string, resId: string): Promise<void> {
    if (busy) return;
    const kind = kindOf(resId);
    if (!kind) return;
    busy = `${projId}:${resId}`;
    try {
      const linked = isLinked(projId, resId);
      if (kind === "db") {
        if (linked) await node.dispatch("db.detach", { target: { id: projId }, options: { secretName: secretFor(projId, resId) } });
        else await node.dispatch("db.connect", { target: { id: projId }, databaseId: resId });
      } else if (kind === "store") {
        if (linked) await node.dispatch("storage.shared.detach", { target: { id: projId }, storeId: resId });
        else await node.dispatch("storage.shared.attach", { target: { id: projId }, storeId: resId });
      } else {
        // domain (1:N) — attach routes / moves the domain to this project; detach unroutes.
        if (linked) await node.dispatch("domain.detach", { target: { id: projId }, domain: resId });
        else await node.dispatch("domain.attach", { target: { id: projId }, domain: resId });
      }
      await reloadLinks(resId, kind); err = null;
    } catch (e) { err = e instanceof Error ? e.message : String(e); }
    finally { busy = ""; }
  }
  function onProject(id: string): void { selProject = selProject === id ? null : id; }
  function onResource(resId: string): void { if (selProject) void toggle(selProject, resId); }
  function key(e: KeyboardEvent, fn: () => void): void { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); } }
  function code(kind: ResKind): string { return kind === "db" ? "DB" : kind === "store" ? "ST" : "DM"; }
  function verb(kind: ResKind, linked: boolean): string { return kind === "domain" ? (linked ? "unroute" : "route") : (linked ? "unlink" : "link"); }

  // ── Layout (hand-rolled SVG, mirrors FleetMapBlock) ──────────────
  const W = 1000;
  const ROW = 72;
  const TOP = 72;
  const PX = 250;   // project column x
  const RX = 750;   // resource column x
  const rows = $derived(Math.max(projects.length, resources.length, 1));
  const H = $derived(TOP + rows * ROW);
  function py(i: number): number { return TOP + i * ROW; }
  function ry(j: number): number { return TOP + j * ROW; }
  const projIndex = $derived(new Map(projects.map((p, i) => [p.id, i] as const)));
  const resIndex = $derived(new Map(resources.map((r, j) => [r.id, j] as const)));

  interface Edge { projId: string; resId: string; x1: number; y1: number; x2: number; y2: number; }
  const edges = $derived.by<Edge[]>(() => {
    const out: Edge[] = [];
    for (const r of resources) {
      const j = resIndex.get(r.id); if (j === undefined) continue;
      for (const l of linksOf(r.id)) {
        const i = projIndex.get(l.projectId); if (i === undefined) continue; // project not in scope
        out.push({ projId: l.projectId, resId: r.id, x1: PX + 16, y1: py(i), x2: RX - 16, y2: ry(j) });
      }
    }
    return out;
  });
  function edgePath(e: Edge): string {
    const mx = (e.x1 + e.x2) / 2;
    return `M ${e.x1} ${e.y1} C ${mx} ${e.y1}, ${mx} ${e.y2}, ${e.x2} ${e.y2}`;
  }
</script>

<section class="compose" class:peek>
  {#if !peek}
    <header class="head">
      <div class="title-wrap">
        <h2>Compose</h2>
        <p class="sub">Your projects and the resources they share — click a project, then a resource, to link or unlink it</p>
      </div>
      <span class="chip">{projects.length} project{projects.length === 1 ? "" : "s"} · {dbs.length} db · {stores.length} store{stores.length === 1 ? "" : "s"} · {domains.length} domain{domains.length === 1 ? "" : "s"}</span>
    </header>
  {/if}

  {#if err}<p class="err">{err}</p>{/if}

  {#if !loaded}
    <p class="muted">Loading topology…</p>
  {:else if projects.length === 0}
    <p class="muted">No projects in scope. Widen the scope to compose.</p>
  {:else}
    <div class="canvas-wrap">
      <svg viewBox="0 0 {W} {H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Compose topology">
        <text class="col" x={PX} y="36" text-anchor="middle">PROJECTS</text>
        <text class="col" x={RX} y="36" text-anchor="middle">RESOURCES</text>

        <!-- edges -->
        <g class="edges">
          {#each edges as e (e.projId + ":" + e.resId)}
            <path
              class="edge"
              class:active={selProject === e.projId}
              class:busy={busy === `${e.projId}:${e.resId}`}
              d={edgePath(e)}
              role="button"
              tabindex="0"
              aria-label="Remove link"
              onclick={() => void toggle(e.projId, e.resId)}
              onkeydown={(ev) => key(ev, () => void toggle(e.projId, e.resId))}
            ><title>Linked — click to unlink</title></path>
          {/each}
        </g>

        <!-- project nodes (left) -->
        {#each projects as p, i (p.id)}
          {@const y = py(i)}
          <g
            class="node proj"
            class:sel={selProject === p.id}
            role="button"
            tabindex="0"
            transform="translate({PX}, {y})"
            onclick={() => onProject(p.id)}
            onkeydown={(ev) => key(ev, () => onProject(p.id))}
          >
            <rect class="mark" x="-16" y="-16" width="32" height="32" rx="8" fill={projectGradient(p.id)} />
            <text class="ini" x="0" y="1" text-anchor="middle" dominant-baseline="middle">{initial(p.name)}</text>
            <text class="lbl end" x="-26" y="1" text-anchor="end" dominant-baseline="middle">{p.name}</text>
            <title>{p.name}{selProject === p.id ? " · selected" : ""}</title>
          </g>
        {/each}

        <!-- resource nodes (right): databases + shared stores -->
        {#each resources as r, j (r.id)}
          {@const y = ry(j)}
          {@const linkedToSel = selProject ? isLinked(selProject, r.id) : false}
          <g
            class="node res {r.kind}"
            class:armed={!!selProject}
            class:linked={linkedToSel}
            role="button"
            tabindex="0"
            transform="translate({RX}, {y})"
            onclick={() => onResource(r.id)}
            onkeydown={(ev) => key(ev, () => onResource(r.id))}
          >
            <rect class="mark resmark" x="-16" y="-16" width="32" height="32" rx="8" />
            <text class="ini" x="0" y="1" text-anchor="middle" dominant-baseline="middle">{code(r.kind)}</text>
            <text class="lbl start" x="26" y="-3" text-anchor="start">{r.label}</text>
            <text class="meta" x="26" y="12" text-anchor="start">{r.sub} · {linksOf(r.id).length} link{linksOf(r.id).length === 1 ? "" : "s"}</text>
            <title>{r.label} ({r.sub}){selProject ? ` · click to ${verb(r.kind, linkedToSel)} ${nameOf(selProject)}` : ""}</title>
          </g>
        {/each}
      </svg>

      {#if resources.length === 0}
        <div class="empty">No resources yet — create a <strong>Database</strong>, a shared <strong>Storage</strong> store, or add a <strong>Domain</strong>, then compose the links here.</div>
      {/if}
    </div>

    {#if !peek}
      <p class="hint">
        {#if selProject}
          <span class="sel-name">{nameOf(selProject)}</span> selected — click a resource to link or unlink it, or click the project again to deselect. Domains route to one app at a time, so clicking a domain moves its route here.
        {:else}
          Click a project to select it, then click a resource (database, shared store, or domain) to link them. Click a link to remove it.
        {/if}
      </p>
    {/if}
  {/if}
</section>

<style>
  .compose { display: flex; flex-direction: column; gap: var(--space-4); max-width: 1180px; font-family: var(--type-font); }
  .compose.peek { gap: var(--space-2); max-width: none; }
  .compose.peek .canvas-wrap { max-height: 340px; overflow: auto; scrollbar-width: thin; }
  .head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-3); }
  .title-wrap { min-width: 0; }
  h2 { margin: 0; font-size: 22px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .sub { margin: 2px 0 0; font-size: 13px; color: var(--text-secondary); line-height: 1.5; }
  .chip { font-size: 11px; font-weight: 500; white-space: nowrap; color: var(--text-secondary); background: var(--bg-hover); border: 0.5px solid var(--border-subtle); border-radius: var(--radius-pill); padding: 4px 10px; }
  .muted { font-size: 13px; color: var(--text-tertiary); }
  .err { margin: 0; font-size: 12.5px; color: var(--error); }

  .canvas-wrap {
    position: relative; border-radius: var(--radius-xl, 18px); overflow: hidden;
    background: radial-gradient(120% 90% at 50% 0%, color-mix(in oklab, var(--accent, #5b8cff) 7%, var(--surface-sunken)) 0%, var(--surface-sunken) 60%);
    border: 0.5px solid var(--border);
  }
  svg { display: block; width: 100%; height: auto; }
  .col { fill: var(--text-ghost); font-size: 11px; font-weight: 500; letter-spacing: 0.08em; }

  .edge { fill: none; stroke: var(--border-strong); stroke-width: 1.5; opacity: 0.5; cursor: pointer; transition: stroke var(--ease-fast), opacity var(--ease-fast), stroke-width var(--ease-fast); }
  .edge:hover { stroke: var(--error); opacity: 0.9; }
  .edge.active { stroke: var(--accent, #5b8cff); opacity: 0.95; stroke-width: 2; }
  .edge.busy { opacity: 0.3; }

  .node { cursor: pointer; }
  .node .mark { transition: filter var(--ease-fast); }
  .node .ini { fill: rgba(255,255,255,0.95); font-size: 11px; font-weight: 600; pointer-events: none; }
  .node .lbl { fill: var(--text-primary); font-size: 13px; font-weight: 500; pointer-events: none; }
  .node .meta { fill: var(--text-tertiary); font-size: 10.5px; font-family: var(--type-mono); pointer-events: none; }
  .node:hover .mark { filter: brightness(1.15); }
  .node:focus-visible { outline: none; }
  .node:focus-visible .mark { stroke: var(--border-focus); stroke-width: 2; }

  .proj.sel .mark { stroke: var(--accent, #5b8cff); stroke-width: 2.5; }
  /* resource marks — database vs shared store get distinct tints */
  .resmark { stroke: var(--border-strong); stroke-width: 1; }
  .res.db .resmark { fill: var(--bg-active); }
  .res.store .resmark { fill: color-mix(in srgb, var(--success) 22%, var(--bg-active)); }
  .res.domain .resmark { fill: color-mix(in srgb, #7b5bff 22%, var(--bg-active)); }
  .res.armed .resmark { stroke: var(--accent, #5b8cff); stroke-dasharray: 3 2; }
  .res.linked.db .resmark { fill: color-mix(in srgb, var(--accent, #5b8cff) 30%, var(--bg-active)); stroke-dasharray: none; }
  .res.linked.store .resmark { fill: color-mix(in srgb, var(--success) 45%, var(--bg-active)); stroke-dasharray: none; }
  .res.linked.domain .resmark { fill: color-mix(in srgb, #7b5bff 45%, var(--bg-active)); stroke-dasharray: none; }

  .empty { position: absolute; inset: 0; display: grid; place-items: center; text-align: center; padding: var(--space-6); font-size: 13px; color: var(--text-tertiary); }
  .empty strong { color: var(--text-secondary); font-weight: 600; }

  .hint { margin: 0; font-size: 12.5px; color: var(--text-tertiary); line-height: 1.5; }
  .sel-name { color: var(--accent, #5b8cff); font-weight: 600; }
</style>
